import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SettingsQueries from '../../../requests/queries/Settings';
import './Settings.css';

export default function Settings() {
    const [stripeApiKey, setStripeApiKey] = useState('');
    const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);
    const [showWebhookSecret, setShowWebhookSecret] = useState(false);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(false);
    const [savingSecret, setSavingSecret] = useState(false);
    const [message, setMessage] = useState({ type: null, text: '' });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await SettingsQueries.getSettings();
                const stripeWebhookSecretData = await SettingsQueries.getStripeWebhookSecret();
                if (stripeWebhookSecretData) {
                    setStripeWebhookSecret(stripeWebhookSecretData.stripeWebhookSecret.key || '');
                }
                if (!cancelled && data) {
                    if (data.stripeApiKey != null) setStripeApiKey(data.stripeApiKey.key || '');
                    if (data.stripeWebhookSecret != null) setStripeWebhookSecret(data.stripeWebhookSecret || '');
                }
            } catch {
                if (!cancelled) setMessage({ type: 'error', text: 'Impossible de charger les paramètres.' });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const showFeedback = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: null, text: '' }), 4000);
    };

    const handleStripeApiKeySave = async () => {
        setSavingKey(true);
        try {
            await SettingsQueries.updateStripeApiKey(stripeApiKey);
            showFeedback('success', 'Clé API Stripe enregistrée.');
        } catch {
            showFeedback('error', 'Erreur lors de l\'enregistrement de la clé API.');
        } finally {
            setSavingKey(false);
        }
    };

    const handleStripeWebhookSecretSave = async () => {
        setSavingSecret(true);
        try {
            await SettingsQueries.updateStripeWebhookSecret(stripeWebhookSecret);
            showFeedback('success', 'Secret du webhook enregistré.');
        } catch {
            showFeedback('error', 'Erreur lors de l\'enregistrement du secret.');
        } finally {
            setSavingSecret(false);
        }
    };

    if (loading) {
        return (
            <div className="settings-page">
                <div className="settings-loading">
                    <div className="settings-spinner" />
                    <p>Chargement des paramètres…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="settings-page">
            <div className="settings-background" aria-hidden="true" />
            <div className="settings-container">
                <Link to="/projects" className="settings-back">← Retour aux projets</Link>

                <header className="settings-header">
                    <div className="settings-header-icon">⚙️</div>
                    <div>
                        <h1 className="settings-title">Paramètres</h1>
                        <p className="settings-subtitle">Gérez les clés et intégrations de votre application</p>
                    </div>
                </header>

                {message.text && (
                    <div className={`settings-toast settings-toast--${message.type}`} role="status">
                        {message.type === 'success' ? '✓' : '!'} {message.text}
                    </div>
                )}

                <section className="settings-card">
                    <div className="settings-card-header">
                        <span className="settings-card-icon">💳</span>
                        <div>
                            <h2 className="settings-card-title">Stripe</h2>
                            <p className="settings-card-desc">Configurez vos clés Stripe pour gérer les paiements</p>
                        </div>
                    </div>

                    <div className="settings-fields">
                        <div className="settings-field">
                            <label htmlFor="stripe-api-key" className="settings-label">Clé API Stripe</label>
                            <div className="settings-input-wrap">
                                <input
                                    id="stripe-api-key"
                                    type={showApiKey ? 'text' : 'password'}
                                    value={stripeApiKey}
                                    onChange={(e) => setStripeApiKey(e.target.value)}
                                    className="settings-input"
                                    placeholder="sk_live_... ou sk_test_..."
                                    autoComplete="off"
                                />
                                <button
                                    type="button"
                                    className="settings-toggle-password"
                                    onClick={() => setShowApiKey((v) => !v)}
                                    title={showApiKey ? 'Masquer' : 'Afficher'}
                                    aria-label={showApiKey ? 'Masquer la clé' : 'Afficher la clé'}
                                >
                                    {showApiKey ? '🙈' : '👁️'}
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={handleStripeApiKeySave}
                                disabled={savingKey}
                                className="settings-btn settings-btn--primary"
                            >
                                {savingKey ? 'Enregistrement…' : 'Enregistrer la clé API'}
                            </button>
                        </div>

                        <div className="settings-divider" />

                        <div className="settings-field">
                            <label htmlFor="stripe-webhook-secret" className="settings-label">Secret du webhook Stripe</label>
                            <div className="settings-input-wrap">
                                <input
                                    id="stripe-webhook-secret"
                                    type={showWebhookSecret ? 'text' : 'password'}
                                    value={stripeWebhookSecret}
                                    onChange={(e) => setStripeWebhookSecret(e.target.value)}
                                    className="settings-input"
                                    placeholder="whsec_..."
                                    autoComplete="off"
                                />
                                <button
                                    type="button"
                                    className="settings-toggle-password"
                                    onClick={() => setShowWebhookSecret((v) => !v)}
                                    title={showWebhookSecret ? 'Masquer' : 'Afficher'}
                                    aria-label={showWebhookSecret ? 'Masquer le secret' : 'Afficher le secret'}
                                >
                                    {showWebhookSecret ? '🙈' : '👁️'}
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={handleStripeWebhookSecretSave}
                                disabled={savingSecret}
                                className="settings-btn settings-btn--primary"
                            >
                                {savingSecret ? 'Enregistrement…' : 'Enregistrer le secret'}
                            </button>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}
